import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { FiSearch } from "react-icons/fi";
import "../css/cards.css";

const LAST_USED_OPTIONS = [
  { value: "all", label: "Any time", hours: null },
  { value: "1h", label: "Last hour", hours: 1 },
  { value: "24h", label: "Last 24 hours", hours: 24 },
  { value: "72h", label: "Last 3 days", hours: 72 },
  { value: "168h", label: "Last 7 days", hours: 168 },
];

const RecentQuestionsCard = ({ activity, onInspectSession }) => {
  const feed = useMemo(
    () => (Array.isArray(activity) ? activity : []),
    [activity]
  );
  const [loadingId, setLoadingId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [lastUsedFilter, setLastUsedFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const filteredItems = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const windowConfig =
      LAST_USED_OPTIONS.find((option) => option.value === lastUsedFilter) ||
      LAST_USED_OPTIONS[0];
    const hours = windowConfig?.hours;
    const windowMs = hours ? hours * 60 * 60 * 1000 : null;
    const nowMs = Date.now();
    return feed.filter((entry) => {
      const name = (entry?.name || "").toLowerCase();
      let ageMs = typeof entry?.ageMs === "number" ? entry.ageMs : null;
      if (ageMs == null) {
        let timestampMs = null;
        if (typeof entry?.timestampMs === "number") {
          timestampMs = entry.timestampMs;
        } else if (entry?.absoluteTimestamp) {
          const parsed = new Date(entry.absoluteTimestamp).getTime();
          if (!Number.isNaN(parsed)) {
            timestampMs = parsed;
          }
        }
        if (timestampMs != null) {
          ageMs = Math.max(0, nowMs - timestampMs);
        }
      }
      const searchMatch =
        normalizedSearch.length === 0 || name.includes(normalizedSearch);
      const timeMatch =
        !windowMs ||
        (typeof ageMs === "number" && ageMs <= windowMs) ||
        ageMs == null;
      return searchMatch && timeMatch;
    });
  }, [
    feed,
    lastUsedFilter,
    searchTerm,
  ]);

  const items = filteredItems.slice(0, 5);

  const handleInspect = async (item) => {
    if (
      !item?.sessionId ||
      !item?.username ||
      typeof onInspectSession !== "function"
    ) {
      return;
    }
    setErrorMessage("");
    setLoadingId(item.sessionId);
    try {
      await Promise.resolve(
        onInspectSession({
          sessionId: item.sessionId,
          sessionOwner: item.username,
          studentName: item.name,
          snippet: item.action,
        })
      );
    } catch (error) {
      setErrorMessage(
        error?.message || "Unable to open this conversation right now."
      );
    } finally {
      setLoadingId("");
    }
  };

  return (
    <div className="chart-card">
      <h3 className="chart-title">Recent Activity</h3>
      <p className="chart-description">
        The latest conversations kicked off by your students. Use them to spot
        confusion in real time.
      </p>
      <div className="recent-activity-filters">
        <div className="recent-filter-search">
          <FiSearch aria-hidden="true" />
          <input
            type="search"
            placeholder="Search student"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <label className="recent-filter-group">
          <span>Last used</span>
          <div className="recent-filter-control">
            <select
              value={lastUsedFilter}
              onChange={(event) => setLastUsedFilter(event.target.value)}
            >
              {LAST_USED_OPTIONS.map((option) => (
                <option value={option.value} key={`recent-window-${option.value}`}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </label>
      </div>
      {items.length ? (
        <>
          <ul className="recent-questions-list">
            {items.map((entry, index) => {
              const clickable =
                entry.sessionId && entry.username && onInspectSession;
              const isLoading = loadingId === entry.sessionId;
              return (
                <li key={`recent-question-${index}`}>
                  <button
                    type="button"
                    className={`recent-question-block ${
                      clickable ? "" : "disabled"
                    }`}
                    onClick={() => handleInspect(entry)}
                    disabled={!clickable || isLoading}
                  >
                    <div className="recent-question-head">
                      <strong>{entry.name || "Student"}</strong>
                      <span>{entry.timestamp || "Just now"}</span>
                    </div>
                    <p>{entry.action || "Asked a new question"}</p>
                    {clickable && (
                      <span className="recent-question-cta">
                        {isLoading ? "Opening…" : "View conversation"}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          {errorMessage && (
            <p className="activity-session-error">{errorMessage}</p>
          )}
        </>
      ) : (
        <p className="dashboard-modal-empty compact">
          {feed.length
            ? "No recent activity matches the selected filters."
            : "No new questions have been recorded lately."}
        </p>
      )}
    </div>
  );
};

RecentQuestionsCard.propTypes = {
  activity: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      action: PropTypes.string,
      timestamp: PropTypes.string,
      sessionId: PropTypes.string,
      username: PropTypes.string,
      solutionLevel: PropTypes.string,
      solutionType: PropTypes.string,
      absoluteTimestamp: PropTypes.string,
      timestampMs: PropTypes.number,
      ageMs: PropTypes.number,
    })
  ),
  onInspectSession: PropTypes.func,
};

export default RecentQuestionsCard;
